import {inject, Injectable} from '@angular/core';
import {Observable, retry} from "rxjs";
import {
  DownloadExcelRequestParams,
  GetSelectDataParams,
  RecordData,
  RecordsDataRequestQueryParams,
  RecordTableParams,
  RecordTotalData,
  RecordUpdateRequestParams,
  SelectData
} from "../types/records-table.types";
import {baseUrl} from "../constants/base-url";
import {Pageable} from "../types/pagable.types";
import {HttpClient} from "@angular/common/http";
import {UserChatQueryParams} from "../../features/pages/telegram-page/types/telegram-page.types";
import {TelegramNotifications} from "../types/telegram-notifications.types";
import {ChatHistoryParams} from "../types/chat-history.types";
import {jsonPathHeaders, octetStreamHeaders} from "../models/http-headers";

@Injectable({
  providedIn: 'root'
})
export class RecordTableApiService {
  private http: HttpClient = inject(HttpClient);

  public getRecordTableParams(path: string): Observable<RecordTableParams> {
    return this.http.get<RecordTableParams>(`${baseUrl}/${path}/meta`);
  }

  public fetchRecordTableData(path: string, queryParams: RecordsDataRequestQueryParams): Observable<Pageable<RecordData>> {
    return this.http.get<Pageable<RecordData>>(`${baseUrl}/${path}/p`, {
      params: queryParams
    }).pipe(retry(1));
  }

  public getRecordTableTotals(path: string, queryParams: RecordsDataRequestQueryParams): Observable<Array<RecordTotalData>> {
    return this.http.get<Array<RecordTotalData>>(`${baseUrl}/${path}/columns-aggregation`, {
      params: queryParams
    });
  }

  public addNewRecord(path: string, newRecordData: Partial<RecordData>): Observable<RecordData> {
    return this.http.post<RecordData>(`${baseUrl}/${path}`, newRecordData);
  }

  public deleteRecord(path: string, recordId: string | number): Observable<void> {
    return this.http.delete<void>(`${baseUrl}/${path}/${recordId}`);
  }

  public updateRecord<T>(updateRecordParams: RecordUpdateRequestParams): Observable<T> {
    const { path, recordId, fieldName, value, showMistakes } = updateRecordParams;

    const patchOperations = [{
      'op': 'add',
      'path': `/${fieldName}`,
      'value': value !== undefined ? value : null,
      showMistakes: showMistakes
    }];

    return this.http.patch<T>(`${baseUrl}/${path}/${recordId}`, patchOperations, {
      headers: jsonPathHeaders
    });
  }

  public downloadExcel(downloadExcelRequestParams: DownloadExcelRequestParams): Observable<BlobPart> {
    const { path, queryParams, excelType } = downloadExcelRequestParams;

    return this.http.get<BlobPart>(`${baseUrl}/${path}/download/excel`, {
      headers: octetStreamHeaders,
      responseType: 'arraybuffer' as 'json',
      params: {...queryParams, excelType},
    });
  }

  public generateReports(path: string): Observable<void> {
    return this.http.post<void>(`${baseUrl}/${path}/generate`, {});
  }

  public fetchSelectData({ propertyLazySelect, id, value }: GetSelectDataParams): Observable<Array<SelectData>> {
    const body = {
      'id': id,
      'resourceName': propertyLazySelect.recourseName,
      'providerType': propertyLazySelect.providerType,
      'value': value
    };

    return this.http.post<Array<SelectData>>(`${baseUrl}/select-data/search`, body, {
      headers: jsonPathHeaders,
    });
  }

  public getTelegramChat(id: number): Observable<UserChatQueryParams> {
    return this.http.get<UserChatQueryParams>(`${baseUrl}/record-calendars/${id}/telegram/chats`);
  }

  public getTelegramNotifications(recordCalendarIds: Array<number>): Observable<Array<TelegramNotifications>> {
    return this.http.post<Array<TelegramNotifications>>(`${baseUrl}/telegram-notifications`, recordCalendarIds);
  }

  public getTelegramChatHistory(recordCalendarIds: Array<number>): Observable<Array<ChatHistoryParams>> {
    return this.http.post<Array<ChatHistoryParams>>(`${baseUrl}/telegram/chat-history`, recordCalendarIds);
  }
}
